import { useState, useEffect } from "react";
import { useMemo, useCallback } from "react";
import { useAuth } from "../../AuthContext";
import { useLocation, useParams } from "react-router-dom";
import SoftBox from "components/SoftBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Sidenav from "layouts/interviewSection/components/Sidenav";
import BasicInfo from "layouts/interviewSection/components/BasicInfo";
import JobRelatedDataForm from "layouts/interviewSection/components/JobRelatedDataForm";
import EducationalDetails from "layouts/interviewSection/components/EducationalDetails";
import SalaryStructure from "layouts/interviewSection/components/SalaryStructure";
import UploadDocuments from "layouts/interviewSection/components/UploadDocuments";
import AccountDetails from "layouts/interviewSection/components/AccountDetails";
import ChangeUserPassword from "layouts/interviewSection/components/ChangeUserPassword";
import { API_URL } from "config";
import { getUserById } from "api/users";
import { documentFields } from "layouts/interviewSection/constant/documentFields";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import CircularProgress from "@mui/material/CircularProgress";
import SoftSnackbar from "components/SoftSnackbar";
import axios from "axios";

function InterviewSection() {
  /* ================= PERMISSION SETUP ================= */
  const { permissions: ctxPerms = [], isAuthReady } = useAuth();

  const storedPerms = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("permissions") || "[]");
    } catch {
      return (localStorage.getItem("permissions") || "").split(",").filter(Boolean);
    }
  }, []);

  const permSet = useMemo(() => {
    const perms = ctxPerms.length ? ctxPerms : storedPerms;
    return new Set(perms.map((p) => (typeof p === "string" ? p : p.name)));
  }, [ctxPerms, storedPerms]);

  const hasSectionPermission = useCallback(
    (sectionName) => permSet.has(`UserProfile.${sectionName}`),
    [permSet]
  );
  /* ==================================================== */

  const { id } = useParams();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  const [selectData, setSelectData] = useState({
    managers: [],
    team_leaders: [],
    shifts: [],
  });

  const [basicInfo, setBasicInfo] = useState({});
  const [jobData, setJobData] = useState({});
  const [jobDataLoading, setJobDataLoading] = useState(false);
  const [education, setEducation] = useState({});
  const [educationLoading, setEducationLoading] = useState(false);
  const [salary, setSalary] = useState({});
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [documents, setDocuments] = useState({});
  const [account, setAccount] = useState({});
  const [accountLoading, setAccountLoading] = useState(false);
  const [password, setPassword] = useState({});
  const [allData, setAllData] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "info",
  });

  const showSnack = useCallback((message, color = "info") => {
    setSnackbar({ open: true, message, color });
  }, []);

  const getMessageFromData = useCallback((data, fallback) => {
    if (data?.errors && typeof data.errors === "object") {
      return Object.entries(data.errors)
        .map(([field, messages]) => {
          const text = Array.isArray(messages) ? messages.join(", ") : messages;
          return `${field}: ${text}`;
        })
        .join("; ");
    }

    return data?.message || data?.error || fallback;
  }, []);

  const parseFetchResponse = useCallback(
    async (response, fallback) => {
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(getMessageFromData(data, fallback));
      }

      return data;
    },
    [getMessageFromData]
  );

  const getAxiosErrorMessage = useCallback(
    (error, fallback) => getMessageFromData(error?.response?.data, error?.message || fallback),
    [getMessageFromData]
  );

  const hydrateUserResponse = useCallback((response) => {
    const user = response?.user || {};
    const dropdowns = response?.dropdowns || {};

    setAllData(user);
    setBasicInfo(user);
    setJobData(user);
    setEducation(user);
    setSalary(user);
    setAccount(user);
    setPassword(user);
    setDocuments({
      upload_aadhar: user.upload_aadhar,
      upload_pan: user.upload_pan,
      upload_tenth: user.upload_tenth,
      upload_twelth: user.upload_twelth,
      upload_graduation: user.upload_graduation,
      upload_post_graduation: user.upload_post_graduation,
      is_uploaddocuments: user.is_uploaddocuments,
      pf_form: user.pf_form,
      esi_form: user.esi_form,
      pre_exp_letter: user.pre_exp_letter,
      pre_sal_letter: user.pre_sal_letter,
    });

    setSelectData({
      managers: Array.isArray(dropdowns.managers)
        ? dropdowns.managers.map((d) => ({
            label: d.name,
            value: d.id,
          }))
        : [],
      team_leaders: Array.isArray(dropdowns.team_leaders)
        ? dropdowns.team_leaders.map((d) => ({
            label: d.name,
            value: d.id,
          }))
        : [],
      shifts: Array.isArray(dropdowns.shifts)
        ? dropdowns.shifts.map((d) => ({
            label: d.label,
            value: d.id,
          }))
        : [],
    });
  }, []);

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        setIsLoading(true);

        const preloadedResponse = location.state?.preloadedUserResponse;
        const response = Number(preloadedResponse?.user?.id) === Number(id)
          ? preloadedResponse
          : await getUserById(id);

        hydrateUserResponse(response);
      } catch (error) {
        console.error("Error loading user data:", error);
        showSnack(getAxiosErrorMessage(error, "Error loading user data."), "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [getAxiosErrorMessage, hydrateUserResponse, id, location.state, showSnack]);

  const updateBasicInfo = async () => {
    const formData = new FormData();
    const textFields = [
      "name",
      "last_name",
      "dob",
      "email",
      "phone_number",
      "emergency_contact_one",
      "emergency_contact_two",
      "location",
      "current_address",
      "permanent_address",
      "aadhar_number",
      "pan_number",
      "relocate",
      "referrer_name",
      "qualification",
      "employment_status",
      "department_id",
      "designation_id",
      "exp_years",
      "exp_months",
      "notice_period",
      "salary_current",
      "salary_expected",
      "role_id",
      "branch_id",
      "subbranch_id",
      "manager_id",
      "team_leader_id",
      "interview_data",
      "pf_esi",
      "gender",
    ];

    textFields.forEach((field) => {
      formData.append(field, basicInfo[field] || "");
    });

    formData.append("shifts", JSON.stringify(basicInfo.shifts || []));

    if (basicInfo.photo) formData.append("photo", basicInfo.photo);
    if (basicInfo.cv) formData.append("cv", basicInfo.cv);
    if (basicInfo.consent_form) formData.append("consent_form", basicInfo.consent_form);

    for (let pair of formData.entries()) {
      console.log(pair[0] + ":", pair[1]);
    }
  };

  const updateAccount = async () => {
    try {
      setAccountLoading(true);
      const res = await fetch(`${API_URL}/users/account-details/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(account),
      });

      const responseData = await parseFetchResponse(res, "Failed to update account info.");
      showSnack(responseData.message || "Account info updated successfully!", "success");
    } catch (error) {
      console.error("Error:", error);
      showSnack(error.message || "Failed to update account info.", "error");
    } finally {
      setAccountLoading(false);
    }
  };

  const updateChangePassword = async () => {
    try {
      const res = await fetch(`${API_URL}/users/change-password/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(password),
      });

      const responseData = await parseFetchResponse(res, "Failed to update password info.");
      showSnack(responseData.message || "Password updated successfully!", "success");
    } catch (error) {
      console.error("Error:", error);
      showSnack(error.message || "Failed to update password info.", "error");
    }
  };

  const updateSalary = async (payload = salary) => {
    try {
      setSalaryLoading(true);
      const res = await fetch(`${API_URL}/users/salary-details/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseData = await parseFetchResponse(res, "Failed to update salary info.");
      showSnack(responseData.message || "Salary info updated successfully!", "success");
    } catch (error) {
      console.error("Error:", error);
      showSnack(error.message || "Failed to update salary info.", "error");
    } finally {
      setSalaryLoading(false);
    }
  };

  // ✅ Final version of updateDocuments
  const updateDocuments = async () => {
    const formData = new FormData();
    documentFields.forEach(({ key }) => {
      if (documents[key] instanceof File) {
        formData.append(key, documents[key]);
      }
    });

    if ([...formData.entries()].length === 0) {
      showSnack("No new files selected for upload.", "warning");
      return;
    }

    try {
      setDocumentLoading(true);
      const res = await fetch(`${API_URL}/users/documents-details/${id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: formData,
      });

      const responseData = await parseFetchResponse(res, "Failed to upload documents.");
      const updatedPaths = responseData.data;

      setDocuments((prev) => ({
        ...prev,
        ...(updatedPaths || {}),
      }));

      showSnack(responseData.message || "Documents uploaded and saved successfully!", "success");
    } catch (error) {
      console.error("Error updating documents:", error);
      showSnack(error.message || "Failed to upload documents.", "error");
    } finally {
      setDocumentLoading(false);
    }
  };


  const updateJobData = async (payloadFromChild) => {
    try {
      setJobDataLoading(true);

      const res = await axios.put(
        `${API_URL}/users/job-related-data/${id}`,
        payloadFromChild,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      // ❗ Agar backend success nahi hai
      if (res.status !== 200) {
        throw new Error("Job data update failed");
      }

      return res; // ✅ IMPORTANT
    } catch (error) {
      console.error("API ERROR:", error);
      throw error; // 🔥 MOST IMPORTANT LINE
    } finally {
      setJobDataLoading(false);
    }
  };



  const updateEducation = async () => {
    try {
      setEducationLoading(true);
      const res = await fetch(`${API_URL}/users/education-details/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(education),
      });

      const responseData = await parseFetchResponse(res, "Failed to update education info.");
      showSnack(responseData.message || "Education info updated successfully!", "success");
    } catch (error) {
      console.error("Error:", error);
      showSnack(error.message || "Failed to update education info.", "error");
    } finally {
      setEducationLoading(false);
    }
  };

  return (
    <DashboardLayout sx={{ overflow: "hidden" }}>
      <DashboardNavbar />
      <SoftBox display="flex" sx={{ overflow: "hidden" }}>
        {/* Sidenav */}
        <div style={{ width: "250px", flexShrink: 0 }}>
          <Sidenav basicInfo={basicInfo} />
        </div>

        {/* Main Content Area */}
        <div
          className="flex-grow-1 overflow-auto p-3"
          style={{
            height: "100vh",
            backgroundColor: "#f9f9f9",
            display: "flex",
            justifyContent: isLoading ? "center" : "flex-start",
            alignItems: isLoading ? "center" : "stretch",
          }}
        >
          {isLoading || !isAuthReady ? (
            <div className="text-center w-100">
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="mt-3">Loading user data...</p>
            </div>
          ) : (
            <div className="container-fluid">
              {/* Basic Info */}
              {hasSectionPermission("BasicInfo") && (
                <div className="row mb-4" id="basic-info" style={{ scrollMarginTop: "300px" }}>
                  <div className="col-12">
                    <BasicInfo
                      value={allData || {}}
                      onChange={setBasicInfo}
                      dropdowns={selectData}
                      onUpdate={updateBasicInfo}
                    />
                  </div>
                </div>
              )}

              {/* Educational Details */}
              {hasSectionPermission("EducationalDetails") && (
                <div className="row mb-4">
                  <div className="col-12">
                    <EducationalDetails
                      value={allData || {}}
                      onChange={setEducation}
                      onUpdate={updateEducation}
                      loading={educationLoading}
                      setLoading={setEducationLoading}
                      onNotify={showSnack}
                    />
                  </div>
                </div>
              )}

              {/* Document Upload */}
              {hasSectionPermission("UploadDocuments") && (
                <div className="row mb-4">
                  <div className="col-12">
                    <UploadDocuments
                      value={documents}
                      onChange={setDocuments}
                      onUpdate={updateDocuments}
                      loading={documentLoading}
                    />
                  </div>
                </div>
              )}

              {/* Job Related Data */}
              {hasSectionPermission("JobRelatedData") && (
                <div className="row mb-4" id="job-related-data">
                  <div className="col-12">
                    <JobRelatedDataForm
                      value={allData || {}}
                      onChange={setJobData}
                      onUpdate={updateJobData}
                      loading={jobDataLoading}
                      setLoading={setJobDataLoading}
                    />
                  </div>
                </div>
              )}

              {/* Salary Structure */}
              {hasSectionPermission("SalaryStructure") && (
                <div className="row mb-4">
                  <div className="col-12">
                    <SalaryStructure
                      value={allData || {}}
                      onChange={setSalary}
                      onUpdate={updateSalary}
                      loading={salaryLoading}
                      setLoading={setSalaryLoading}
                      onNotify={showSnack}
                    />
                  </div>
                </div>
              )}

              {/* Account Details */}
              {hasSectionPermission("AccountDetails") && (
                <div className="row mb-4">
                  <div className="col-12">
                    <AccountDetails
                      value={allData || {}}
                      onChange={setAccount}
                      onUpdate={updateAccount}
                      loading={accountLoading}
                      setLoading={setAccountLoading}
                    />
                  </div>
                </div>
              )}

              {/* Change Password */}
              {hasSectionPermission("ChangePassword") && (
                <div className="row mb-4">
                  <div className="col-12">
                    <ChangeUserPassword
                      value={allData || {}}
                      onChange={setPassword}
                      onUpdate={updateChangePassword}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </SoftBox>
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Notification"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        time={3000}
      />
    </DashboardLayout>
  );
}

export default InterviewSection;
